import { useMemo } from 'react';

import { IOperatingHours } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { ServiceMode } from '@fhs-legacy/frontend/src/state/service-mode';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { StoreProxy, useStoreContext } from '@fhs-legacy/frontend/src/state/store';

interface UseRestaurantOpenStatusResult {
  isRestaurantOpen: boolean;
}

/**
 * Hook to check if restaurant is currently open based on getRestaurant
 * store hours and current time
 */
export function useRestaurantOpenStatus(): UseRestaurantOpenStatusResult {
  const { store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();

  return useMemo(() => {
    const now = new Date();

    // early return if no store or service mode
    if (!store || !serviceMode) {
      return { isRestaurantOpen: false };
    }

    const { availableStoreHours } = getAvailableStoreHours(store, serviceMode);

    if (!availableStoreHours) {
      return { isRestaurantOpen: false };
    }

    const { openTime, closeTime } = getTodayHours(availableStoreHours);

    if (!openTime || !closeTime) {
      return { isRestaurantOpen: false };
    }

    const opensAt = getMinutesFromMidnight(openTime);
    const closesAt = getMinutesFromMidnight(closeTime);

    if (opensAt === null || closesAt === null) {
      return { isRestaurantOpen: false };
    }

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const isCurrentlyOpen = currentMinutes >= opensAt && currentMinutes < closesAt;

    if (!isCurrentlyOpen) {
      return { isRestaurantOpen: false };
    }

    return {
      isRestaurantOpen: true,
    };
  }, [store, serviceMode]);
}

function getAvailableStoreHours(
  store: StoreProxy,
  serviceMode: ServiceMode
): { availableStoreHours: IOperatingHours | null } {
  let availableStoreHours: IOperatingHours | null = null;
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      availableStoreHours = store?.driveThruHours || null;
      break;
    case ServiceMode.CURBSIDE:
      availableStoreHours = store?.curbsideHours || null;
      break;
    case ServiceMode.PICKUP_WINDOW:
      availableStoreHours = store?.pickupWindowHours || null;
      break;
    case ServiceMode.EAT_IN:
    case ServiceMode.TAKEOUT:
    default:
      availableStoreHours = store?.diningRoomHours || null;
  }

  return { availableStoreHours };
}

/**
 * Gets today's opening and closing hours { monOpen, monClose, tueOpen, tueClose, ... }
 */
function getTodayHours(hours: IOperatingHours): { openTime: string; closeTime: string } {
  const now = new Date();
  const dayOfWeek = now.getDay();

  // Response is "thr" not "thu"
  const dayAbbreviations = ['sun', 'mon', 'tue', 'wed', 'thr', 'fri', 'sat'] as const;
  const dayKey = dayAbbreviations[dayOfWeek];

  const openTime = hours[`${dayKey}Open` as keyof IOperatingHours] as string;
  const closeTime = hours[`${dayKey}Close` as keyof IOperatingHours] as string;

  return { openTime, closeTime };
}

function getMinutesFromMidnight(timeString: string): number | null {
  const parts = timeString.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}
